/**
 * contexts/SettingsContext.tsx
 * ------------------------------------------------------------------
 * Loads the company-settings singleton (`settings/company`) and makes
 * it available app-wide. Also exposes a `currentVatSnapshot()` helper
 * — the single place any module asks "what VAT reality should a
 * document issued right now be born under?". That keeps the dormant-
 * VAT logic in exactly one place.
 * ------------------------------------------------------------------
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { setDoc, db } from '@/lib/firebase';
import {
  COLLECTIONS,
  DEFAULT_COMPANY_SETTINGS,
  SETTINGS_DOC_ID,
  type CompanySettings,
  type VatSnapshot,
} from '@/types';

interface SettingsState {
  settings: CompanySettings | null;
  /** True before the settings doc has loaded the first time. */
  loading: boolean;
  /** True if no settings doc exists yet (first-run). */
  uninitialised: boolean;
  /** Persist a full settings document. */
  save: (next: CompanySettings) => Promise<void>;
  /** The VAT reality a document issued NOW should capture. */
  currentVatSnapshot: () => VatSnapshot;
}

const SettingsContext = createContext<SettingsState | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [uninitialised, setUninitialised] = useState(false);

  useEffect(() => {
    const ref = doc(db, COLLECTIONS.settings, SETTINGS_DOC_ID);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setSettings(snap.data() as CompanySettings);
          setUninitialised(false);
        } else {
          setSettings(null);
          setUninitialised(true);
        }
        setLoading(false);
      },
      (err) => {
        console.error('[settings] snapshot error', err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  const value = useMemo<SettingsState>(
    () => ({
      settings,
      loading,
      uninitialised,
      save: async (next) => {
        const ref = doc(db, COLLECTIONS.settings, SETTINGS_DOC_ID);
        await setDoc(ref, next);
      },
      currentVatSnapshot: () => {
        const s = settings ?? DEFAULT_COMPANY_SETTINGS;
        if (s.vatRegistered) {
          return {
            registered: true,
            rate: s.vatRate,
            vatNumber: s.vatNumber,
          };
        }
        return { registered: false, rate: s.vatRate };
      },
    }),
    [settings, loading, uninitialised],
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsState {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
